import React, { useState, useEffect } from 'react';
import { 
  Link2, 
  Mail, 
  Plus, 
  X, 
  Copy, 
  Check,
  ExternalLink,
  Globe,
  Lock,
  Calendar,
  Trash2,
  User,
  LayoutGrid,
  Grid3x3,
  Columns
} from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button/Button';
import { Input } from '@/components/ui/Input/Input';
import { Badge } from '@/components/ui/Badge/Badge';
import { Switch } from '@/components/ui';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs';
import { useNotificationStore } from '@/stores/notificationStore';
import { 
  proposalService, 
  type ProposalShareLink, 
  type ShareLinkType,
  type CreateShareLinkInput,
  type OverviewLayoutType
} from '@/services/ProposalService';

interface ShareLinkConfigDialogProps {
  isOpen: boolean;
  onClose: () => void;
  eventId: string;
  linkType: ShareLinkType;
  existingLinks?: ProposalShareLink[];
  onLinkCreated?: (link: ProposalShareLink, url: string) => void;
  onLinkUpdated?: (link: ProposalShareLink) => void;
  onLinkDeleted?: (linkId: string) => void;
}

// Check if an entry is an email or domain
function isEmail(entry: string): boolean {
  return entry.includes('@');
}

export function ShareLinkConfigDialog({
  isOpen,
  onClose,
  eventId,
  linkType,
  existingLinks = [],
  onLinkCreated,
  onLinkUpdated,
  onLinkDeleted,
}: ShareLinkConfigDialogProps) {
  const notificationStore = useNotificationStore();
  
  // Filter links by type
  const linksOfType = existingLinks.filter(l => l.link_type === linkType);
  const activeLink = linksOfType.find(l => l.is_active);
  
  // Get shared whitelist from any existing link (they're synced)
  const anyActiveLink = existingLinks.find(l => l.is_active);
  
  // Form state - entries can be domains OR emails (shared across all links)
  const [entries, setEntries] = useState<string[]>([]);
  const [newEntry, setNewEntry] = useState('');
  const [expiresAt, setExpiresAt] = useState<string>('');
  const [overviewLayout, setOverviewLayout] = useState<OverviewLayoutType | null>('multi-column'); // Default to columns
  const [isPublic, setIsPublic] = useState<boolean>(false);
  
  // UI state
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [generatedUrl, setGeneratedUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState<'access' | 'customize'>('access');
  
  // Load shared whitelist from any existing link
  useEffect(() => {
    if (isOpen) {
      // Use whitelist from current link, or any other active link (they're shared)
      const sourceLink = activeLink || anyActiveLink;
      if (sourceLink) {
        setEntries(sourceLink.whitelisted_email_domains || []);
        setIsPublic(sourceLink.is_public ?? true); // Default to public (anyone with link)
      } else {
        setEntries([]);
        setIsPublic(true); // Default to public (anyone with link) for new links
      }
      
      // Set expiration, layout, and URL from current link type only
      if (activeLink) {
        setExpiresAt(activeLink.expires_at ? activeLink.expires_at.split('T')[0] : '');
        setOverviewLayout(activeLink.overview_layout || 'multi-column'); // Default to columns
        setGeneratedUrl(`${window.location.origin}/${linkType === 'proposal' ? 'p' : 'r'}/${activeLink.token}`);
      } else {
        setExpiresAt('');
        setOverviewLayout('multi-column'); // Default to columns
        setGeneratedUrl(null);
      }
      setCopied(false);
      setActiveTab('access');
    }
  }, [isOpen, activeLink, anyActiveLink, linkType]);
  
  // Entry management (domains or emails)
  function addEntry() {
    const entry = newEntry.trim().toLowerCase();
    if (!entry || entries.includes(entry)) {
      setNewEntry('');
      return;
    }
    
    // Validate based on type
    if (entry.includes('@')) {
      // Email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(entry)) {
        notificationStore.error('Invalid email format. Use format: user@example.com', { title: 'Invalid Email' });
        return;
      }
    } else {
      // Domain validation
      if (!entry.includes('.')) {
        notificationStore.error('Invalid domain format. Use format: example.com', { title: 'Invalid Domain' });
        return;
      }
    }
    
    setEntries([...entries, entry]);
    setNewEntry('');
  }
  
  function removeEntry(index: number) {
    setEntries(entries.filter((_, i) => i !== index));
  }
  
  async function handleCreateLink() {
    if (!isPublic && entries.length === 0) {
      notificationStore.error('Please add at least one email domain or address for protected links', { title: 'Validation Error' });
      return;
    }
    
    try {
      setIsCreating(true);
      
      const input: CreateShareLinkInput = {
        link_type: linkType,
        whitelisted_email_domains: isPublic ? [] : entries,
        is_public: isPublic,
        expires_at: expiresAt ? new Date(expiresAt).toISOString() : null,
        overview_layout: overviewLayout || 'multi-column', // Default to columns
      };
      
      const result = await proposalService.createShareLink(eventId, input);
      const fullUrl = `${window.location.origin}${result.url}`;
      setGeneratedUrl(fullUrl);
      
      notificationStore.success(`${linkType === 'proposal' ? 'Proposal' : 'Research'} link created!`, { title: 'Link Generated' });
      onLinkCreated?.(result.link, fullUrl);
    } catch (error: any) {
      console.error('Failed to create share link:', error);
      notificationStore.error(error?.message || 'Failed to create link', { title: 'Error' });
    } finally {
      setIsCreating(false);
    }
  }
  
  async function handleUpdateLink() {
    if (!activeLink) return;
    
    if (!isPublic && entries.length === 0) {
      notificationStore.error('Please add at least one email domain or address for protected links', { title: 'Validation Error' });
      return;
    }
    
    try {
      setIsUpdating(true);
      
      const updatedLink = await proposalService.updateShareLink(eventId, activeLink.id, {
        whitelisted_email_domains: isPublic ? [] : entries,
        is_public: isPublic,
        expires_at: expiresAt ? new Date(expiresAt).toISOString() : null,
        overview_layout: overviewLayout || 'multi-column', // Default to columns
      });
      
      notificationStore.success('Link settings updated', { title: 'Success' });
      onLinkUpdated?.(updatedLink);
    } catch (error: any) {
      console.error('Failed to update share link:', error);
      notificationStore.error(error?.message || 'Failed to update link', { title: 'Error' });
    } finally {
      setIsUpdating(false);
    }
  }
  
  async function handleDeleteLink() {
    if (!activeLink) return;
    
    try {
      setIsDeleting(true);
      
      await proposalService.deleteShareLink(eventId, activeLink.id);
      
      notificationStore.success('Link deleted', { title: 'Success' });
      onLinkDeleted?.(activeLink.id);
      setGeneratedUrl(null);
      // Keep entries for potential re-creation
    } catch (error: any) {
      console.error('Failed to delete share link:', error);
      notificationStore.error(error?.message || 'Failed to delete link', { title: 'Error' });
    } finally {
      setIsDeleting(false);
    }
  }
  
  function copyToClipboard() {
    if (generatedUrl) {
      navigator.clipboard.writeText(generatedUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      notificationStore.success('Link copied to clipboard', { title: 'Copied' });
    }
  }
  
  // Separate domains and emails for display
  const domains = entries.filter(e => !isEmail(e));
  const emails = entries.filter(e => isEmail(e));
  
  const title = linkType === 'proposal' ? 'Proposal Link' : 'Research Link';
  const description = linkType === 'proposal' 
    ? 'Share your proposal with the client'
    : 'Share your venue research with the client';
  
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Configure ${title}`}
      description={description}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
          {activeLink ? (
            <>
              <Button 
                variant="outline" 
                onClick={handleDeleteLink}
                isLoading={isDeleting}
                leftIcon={<Trash2 className="w-4 h-4" />}
              >
                Delete Link
              </Button>
              <Button 
                variant="primary" 
                onClick={handleUpdateLink}
                isLoading={isUpdating}
                leftIcon={<Check className="w-4 h-4" />}
              >
                Update Settings
              </Button>
            </>
          ) : (
            <Button 
              variant="primary" 
              onClick={handleCreateLink}
              isLoading={isCreating}
              disabled={!isPublic && entries.length === 0}
              leftIcon={<Link2 className="w-4 h-4" />}
            >
              Generate Link
            </Button>
          )}
        </>
      }
    >
      <div className="space-y-6">
        {/* Generated Link Display */}
        {generatedUrl && (
          <div className="p-4 bg-success-50 border border-success-200 rounded-lg space-y-3">
            <div className="flex items-center gap-2">
              <Check className="w-5 h-5 text-success-600" />
              <span className="font-medium text-success-800">Link Active</span>
            </div>
            <div className="flex items-center gap-2">
              <Input
                value={generatedUrl}
                readOnly
                className="flex-1 bg-white"
              />
              <Button 
                variant="secondary" 
                size="sm"
                onClick={copyToClipboard}
                leftIcon={copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              >
                {copied ? 'Copied' : 'Copy'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.open(generatedUrl, '_blank')}
                leftIcon={<ExternalLink className="w-4 h-4" />}
              >
                Open
              </Button>
            </div>
          </div>
        )}
        
        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'access' | 'customize')}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="access">
              <Lock className="w-4 h-4 mr-2" />
              Access
            </TabsTrigger>
            <TabsTrigger value="customize">
              <LayoutGrid className="w-4 h-4 mr-2" />
              Customize
            </TabsTrigger>
          </TabsList>
          
          {/* Access Tab */}
          <TabsContent value="access" className="space-y-6 mt-4">
            {/* Access Type Toggle */}
            <div className="p-4 bg-gray-50 rounded-lg space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {isPublic ? (
                    <Globe className="w-4 h-4 text-primary-600" />
                  ) : (
                    <Lock className="w-4 h-4 text-gray-500" />
                  )}
                  <label className="text-sm font-medium text-gray-700">
                    Access Control
                  </label>
                </div>
                <Switch
                  checked={isPublic}
                  onChange={(e) => setIsPublic(e.target.checked)}
                  label={isPublic ? 'Anyone with link' : 'Protected'}
                />
              </div>
              <p className="text-xs text-muted-foreground">
                {isPublic 
                  ? 'Anyone with the link can access this content without authentication.'
                  : 'Only users with whitelisted email domains can access this content.'}
              </p>
            </div>

            {/* Access List - Only show if protected */}
            {!isPublic && (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-gray-500" />
                  <label className="block text-sm font-medium text-gray-700">
                    Who Can Access
                  </label>
                  {anyActiveLink && !activeLink && (
                    <Badge variant="secondary" className="text-xs">Shared</Badge>
                  )}
                </div>
              
              <p className="text-sm text-muted-foreground">
                Add email domains (like <code className="bg-gray-100 px-1 rounded">company.com</code>) or specific emails (like <code className="bg-gray-100 px-1 rounded">john@gmail.com</code>). 
                {existingLinks.length > 0 && (
                  <span className="text-primary-600 font-medium"> Access list is shared across all links.</span>
                )}
              </p>
              
              {/* Add Entry Input */}
              <div className="flex gap-2">
                <Input
                  value={newEntry}
                  onChange={(e) => setNewEntry(e.target.value)}
                  placeholder="company.com or user@gmail.com"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addEntry())}
                />
                <Button 
                  variant="secondary" 
                  size="sm" 
                  onClick={addEntry}
                  disabled={!newEntry.trim()}
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              
              {/* Entries Display */}
              {entries.length > 0 ? (
                <div className="space-y-3">
                  {/* Domains Section */}
                  {domains.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                        Domains ({domains.length})
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {domains.map((domain) => (
                          <Badge 
                            key={`domain-${domain}`} 
                            variant="secondary"
                            className="flex items-center gap-1 py-1"
                          >
                            <Globe className="w-3 h-3" />
                            @{domain}
                            <button 
                              onClick={() => removeEntry(entries.indexOf(domain))}
                              className="ml-1 hover:text-red-600"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Emails Section */}
                  {emails.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                        Individual Emails ({emails.length})
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {emails.map((email) => (
                          <Badge 
                            key={`email-${email}`} 
                            variant="primary"
                            className="flex items-center gap-1 py-1"
                          >
                            <User className="w-3 h-3" />
                            {email}
                            <button 
                              onClick={() => removeEntry(entries.indexOf(email))}
                              className="ml-1 hover:text-red-600"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-sm text-warning-600 flex items-center gap-2">
                  <Lock className="w-4 h-4" />
                  Add at least one domain or email to generate the link
                </div>
              )}
              </div>
            )}
            
            {/* Expiration Date */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-gray-500" />
                <label className="block text-sm font-medium text-gray-700">
                  Expiration Date (Optional)
                </label>
              </div>
              <Input
                type="date"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
              />
              <p className="text-sm text-muted-foreground">
                Leave empty for no expiration. Link will remain active until manually disabled.
              </p>
            </div>
            
            {/* Access Info */}
            <div className="p-3 bg-gray-50 rounded-lg text-sm">
              <p className="font-medium text-gray-700 mb-2">How it works:</p>
              <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
                <li>Share the link with your client</li>
                <li>They enter their email address</li>
                <li>If their email or domain is in the access list, they receive a code</li>
                <li>After entering the code, they can access the content for 24 hours</li>
              </ol>
            </div>
          </TabsContent>
          
          {/* Customize Tab */}
          <TabsContent value="customize" className="space-y-6 mt-4">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Destination Display Layout
                </label>
                <p className="text-sm text-muted-foreground mb-3">
                  Choose how destinations are displayed on the overview page.
                </p>
                
                {/* Layout Options */}
                <div className="grid grid-cols-1 gap-2.5">
                  {/* Multi-Column - First */}
                  <button
                    type="button"
                    onClick={() => setOverviewLayout('multi-column')}
                    className={`p-2.5 border-2 rounded-lg text-left transition-all ${
                      (overviewLayout === 'multi-column' || overviewLayout === null)
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center gap-2.5">
                      {/* Left side: Title, Description */}
                      <div className="flex-1 min-w-0 text-left">
                        <div className="flex items-center gap-2 mb-0.5">
                          <h4 className="font-medium text-gray-900 text-sm">Columns</h4>
                          {(overviewLayout === 'multi-column' || overviewLayout === null) && (
                            <Check className="w-4 h-4 text-primary-600 flex-shrink-0" />
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground text-left">
                          Multi-column layout (4 columns on desktop). Compact view for many destinations.
                        </p>
                      </div>
                      {/* Right side: Small Preview */}
                      <div className="w-1/4 flex-shrink-0 flex justify-end">
                        <div className="grid grid-cols-4 gap-0.5 w-16 h-10">
                          <div className="bg-gray-300 rounded"></div>
                          <div className="bg-gray-300 rounded"></div>
                          <div className="bg-gray-300 rounded"></div>
                          <div className="bg-gray-300 rounded"></div>
                        </div>
                      </div>
                    </div>
                  </button>
                  
                  {/* Two-Column Grid - Second */}
                  <button
                    type="button"
                    onClick={() => setOverviewLayout('two-column')}
                    className={`p-2.5 border-2 rounded-lg text-left transition-all ${
                      overviewLayout === 'two-column'
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center gap-2.5">
                      {/* Left side: Title, Description */}
                      <div className="flex-1 min-w-0 text-left">
                        <div className="flex items-center gap-2 mb-0.5">
                          <h4 className="font-medium text-gray-900 text-sm">Grid</h4>
                          {overviewLayout === 'two-column' && (
                            <Check className="w-4 h-4 text-primary-600 flex-shrink-0" />
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground text-left">
                          Clean two-column grid layout. Equal-sized cards in a balanced grid.
                        </p>
                      </div>
                      {/* Right side: Small Preview */}
                      <div className="w-1/4 flex-shrink-0 flex justify-end">
                        <div className="grid grid-cols-2 gap-0.5 w-16 h-10">
                          <div className="bg-gray-300 rounded"></div>
                          <div className="bg-gray-300 rounded"></div>
                          <div className="bg-gray-300 rounded"></div>
                          <div className="bg-gray-300 rounded"></div>
                        </div>
                      </div>
                    </div>
                  </button>
                  
                  {/* Masonry Layout - Third */}
                  <button
                    type="button"
                    onClick={() => setOverviewLayout('masonry')}
                    className={`p-2.5 border-2 rounded-lg text-left transition-all ${
                      overviewLayout === 'masonry'
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center gap-2.5">
                      {/* Left side: Title, Description */}
                      <div className="flex-1 min-w-0 text-left">
                        <div className="flex items-center gap-2 mb-0.5">
                          <h4 className="font-medium text-gray-900 text-sm">Masonry</h4>
                          {overviewLayout === 'masonry' && (
                            <Check className="w-4 h-4 text-primary-600 flex-shrink-0" />
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground text-left">
                          Asymmetric layout with varied card sizes. Best for 3+ destinations with visual hierarchy.
                        </p>
                      </div>
                      {/* Right side: Small Preview */}
                      <div className="w-1/4 flex-shrink-0 flex justify-end">
                        <div className="grid grid-cols-12 gap-0.5 w-16 h-10">
                          <div className="col-span-8 bg-gray-300 rounded"></div>
                          <div className="col-span-4 row-span-2 bg-gray-300 rounded"></div>
                          <div className="col-span-8 bg-gray-300 rounded"></div>
                        </div>
                      </div>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </Modal>
  );
}
